import { motion } from "framer-motion";
import {
    ClipboardList,
    Workflow,
    BarChart3,
    RefreshCw,
    Share2,
    MapPin,
    Globe,
    FileText,
    Plug,
    Target,
    Repeat,
} from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import KineticLines from "@/components/KineticLines";
import SectionHeading from "@/components/SectionHeading";
import CTABand from "@/components/CTABand";
import { IMAGES } from "@/lib/constants";

const GESTAO = [
    {
        icon: ClipboardList,
        titulo: "Diagnóstico e análise",
        texto: "Levantamento do cenário atual, gargalos, riscos e oportunidades.",
    },
    {
        icon: Workflow,
        titulo: "Processos e padronização",
        texto: "Mapeamento de rotinas, organização de fluxos e criação de controles.",
    },
    {
        icon: BarChart3,
        titulo: "Indicadores e desempenho",
        texto: "Definição e acompanhamento de KPIs relevantes para a operação.",
    },
    {
        icon: RefreshCw,
        titulo: "Melhoria contínua",
        texto: "Planos de ação e acompanhamento de mudanças com foco em eficiência.",
    },
];

const DIGITAL = [
    {
        icon: Share2,
        titulo: "Redes sociais",
        texto: "Organização de perfis, planejamento editorial, peças e comunicação coerente com a marca.",
    },
    {
        icon: MapPin,
        titulo: "Google e busca local",
        texto: "Estruturação e otimização da presença da empresa nas pesquisas locais.",
    },
    {
        icon: Globe,
        titulo: "Sites institucionais",
        texto: "Experiências digitais profissionais para apresentar serviços, diferenciais e facilitar contatos.",
    },
    {
        icon: FileText,
        titulo: "Materiais comerciais",
        texto: "Catálogos, apresentações e modelos de propostas alinhados à identidade da empresa.",
    },
    {
        icon: Plug,
        titulo: "Integração entre canais",
        texto: "Integração entre canais digitais e atendimento, do primeiro contato à proposta.",
    },
];

const MODELOS = [
    {
        icon: Target,
        titulo: "Projeto pontual",
        texto: "Estruturação ou implantação de entregáveis definidos, com escopo, prazo e investimento estabelecidos em proposta.",
    },
    {
        icon: Repeat,
        titulo: "Acompanhamento contínuo",
        texto: "Planejamento, execução e aprimoramento recorrentes para empresas que desejam consistência e evolução ao longo do tempo.",
    },
];

const Solucoes = () => (
    <main>
        <Seo
            title="Soluções — Gestão e Processos + Posicionamento Digital | Vorantis"
            description="Gestão e Processos: diagnóstico, processos, indicadores e melhoria contínua. Posicionamento Digital: redes sociais, Google, sites e materiais comerciais."
        />

        <section className="grain-overlay hero-grid-bg relative overflow-hidden bg-navy-deep pb-24 pt-40 md:pb-32 md:pt-48" data-testid="solucoes-hero">
            <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-emerald-500/15 blur-[140px]" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                <motion.div
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                >
                    <span className="h-px w-10 bg-emerald-400" />
                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-emerald-400">Soluções</span>
                </motion.div>
                <h1 className="mt-8 max-w-3xl font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl">
                    <KineticLines
                        testid="solucoes-headline"
                        delay={0.3}
                        lines={["Duas frentes.", <span className="text-emerald-400">Uma visão.</span>]}
                    />
                </h1>
                <motion.p
                    className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.85 }}
                >
                    Gestão e processos para organizar a operação. Posicionamento digital para
                    fortalecer a presença no mercado. Tudo conectado pela mesma estratégia.
                </motion.p>
            </div>
        </section>

        <section className="bg-slate-50 py-24 md:py-32" data-testid="solucoes-gestao-section">
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <div className="grid items-start gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <SectionHeading
                            kicker="01 — Gestão e Processos"
                            title="Clareza para decidir."
                            description="Diagnóstico operacional, mapeamento e melhoria de processos, indicadores, padronização, controles e apoio à tomada de decisão."
                        />
                        <Reveal delay={0.2} className="mt-10">
                            <div className="relative">
                                <div className="absolute -left-3 -top-3 h-full w-full rounded-3xl border border-corp/30" aria-hidden="true" />
                                <img
                                    src={IMAGES.gestao_processos}
                                    alt="Reunião de diagnóstico operacional e mapeamento de processos"
                                    loading="lazy"
                                    className="relative aspect-[4/3] w-full rounded-3xl object-cover"
                                />
                            </div>
                        </Reveal>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
                        {GESTAO.map((item, i) => (
                            <Reveal key={item.titulo} delay={i * 0.08}>
                                <div className="h-full rounded-3xl border border-slate-200 bg-white p-7 transition-colors duration-300 hover:border-corp/40">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-corp-light text-corp">
                                        <item.icon size={20} />
                                    </div>
                                    <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{item.titulo}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.texto}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>

        <section className="grain-overlay bg-navy-deep py-24 md:py-32" data-testid="solucoes-digital-section">
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <div className="grid items-start gap-14 lg:grid-cols-12">
                    <div className="grid gap-5 sm:grid-cols-2 lg:order-1 lg:col-span-7">
                        {DIGITAL.map((item, i) => (
                            <Reveal key={item.titulo} delay={i * 0.08}>
                                <div className="h-full rounded-3xl border border-white/10 bg-navy-surface p-7 transition-colors duration-300 hover:border-emerald-500/40">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                                        <item.icon size={20} />
                                    </div>
                                    <h3 className="mt-5 font-display text-lg font-bold text-white">{item.titulo}</h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-300">{item.texto}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <div className="lg:order-2 lg:col-span-5">
                        <SectionHeading
                            dark
                            kicker="02 — Posicionamento Digital"
                            title="Ser encontrado. Ser escolhido."
                            description="Estratégia de presença digital, identidade de comunicação, Instagram e Facebook, Google, sites e integração com canais de atendimento."
                        />
                        <Reveal delay={0.2} className="mt-10">
                            <div className="relative">
                                <div className="absolute -right-3 -top-3 h-full w-full rounded-3xl border border-emerald-500/40" aria-hidden="true" />
                                <img
                                    src={IMAGES.analytics_growth}
                                    alt="Painel de análise de presença digital e crescimento"
                                    loading="lazy"
                                    className="relative aspect-[4/3] w-full rounded-3xl object-cover"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>

        <section className="bg-white py-24 md:py-32" data-testid="modelos-section">
            <div className="mx-auto max-w-7xl px-6 md:px-10">
                <SectionHeading
                    kicker="Modelos de atuação"
                    title="Soluções sob medida."
                    description="Duas formas de trabalhar com a Vorantis — sempre com escopo claro e proposta adequada à sua realidade."
                />
                <div className="mt-14 grid gap-6 md:grid-cols-2">
                    {MODELOS.map((modelo, i) => (
                        <Reveal key={modelo.titulo} delay={i * 0.12}>
                            <div className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-slate-50 p-9 transition-colors duration-300 hover:border-emerald-500/40 md:p-11">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-navy-deep text-emerald-400 transition-colors duration-300 group-hover:bg-emerald-500 group-hover:text-white">
                                    <modelo.icon size={22} />
                                </div>
                                <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-slate-900">{modelo.titulo}</h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600 md:text-base">{modelo.texto}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <CTABand />
    </main>
);

export default Solucoes;
